<template>
  <v-container fluid>
    <v-btn text small class="mb-3" @click="$router.push('/gestiones-reconocimiento-facial')">
      <v-icon left>mdi-arrow-left</v-icon> Volver a la lista
    </v-btn>

    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="#031D6A" size="48"></v-progress-circular>
    </div>

    <v-alert v-else-if="!gestion" type="error" class="mt-4">
      Gestión no encontrada.
    </v-alert>

    <template v-else>
      <!-- ── Header ─────────────────────────────────────────────────────────── -->
      <v-card tile class="mb-4">
        <v-card-text>
          <v-row align="center" no-gutters>
            <v-col cols="auto" class="mr-4">
              <p class="text-h6 mb-0">Gestión #{{ gestion.id }}</p>
              <p class="text-caption text--secondary mb-0">
                <v-icon x-small>mdi-calendar</v-icon>
                {{ fnDateTime(gestion.createdAt) }}
              </p>
            </v-col>

            <v-col cols="auto">
              <v-chip v-if="gestion.resultado_evaluacion === 'aprobado'" color="green" dark large>
                <v-icon left>mdi-check-circle</v-icon> VERDE — Aprobado
              </v-chip>
              <v-chip v-else-if="gestion.resultado_evaluacion === 'revisión'" color="yellow accent-3" large>
                <v-icon left>mdi-alert-circle</v-icon> AMARILLO — Revisión
              </v-chip>
              <v-chip v-else-if="gestion.resultado_evaluacion === 'rechazado'" color="red" dark large>
                <v-icon left>mdi-close-circle</v-icon> ROJO — Rechazado
              </v-chip>
              <v-chip v-else color="grey lighten-1" large>
                <v-icon left>mdi-clock-outline</v-icon> Sin resultado
              </v-chip>
            </v-col>

          </v-row>
        </v-card-text>
      </v-card>

      <!-- ── Agente de comercio asignado ────────────────────────────────────── -->
      <v-card tile class="mb-4">
        <v-card-title class="subtitle-2 font-weight-bold pb-0">
          <v-icon left color="#031D6A">mdi-account-tie</v-icon>
          Agente de comercio asignado
        </v-card-title>
        <v-divider class="mt-2"></v-divider>
        <v-card-text class="pt-2">
          <v-row align="center" no-gutters>
            <v-col cols="auto" class="mr-3">
              <v-chip v-if="gestion.agente_de_comercio_asignado_id" color="#031D6A" dark small>
                <v-icon left small>mdi-account</v-icon>
                {{ gestion.agente_de_comercio_asignado_nombre }}
              </v-chip>
              <span v-else class="text--disabled">Sin agente asignado</span>
            </v-col>
            <v-col cols="auto" class="mr-3">
              <span v-if="gestion.agente_de_comercio_asignado_id" class="text-caption text--secondary">
                <template v-if="gestion.agente_de_comercio_asignado_por_usuario === 'sistema'">
                  Asignado por: Sistema
                </template>
                <template v-else>
                  Asignado por: {{ gestion.agente_de_comercio_asignado_por_usuario }}
                  el {{ fnFechaAsignacion(gestion.agente_de_comercio_asignado_fecha) }}
                </template>
              </span>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto">
              <v-btn x-small outlined color="#031D6A" @click="fnAbrirDialogAsignarAgente">
                <v-icon left small>mdi-account-edit</v-icon>
                {{ gestion.agente_de_comercio_asignado_id ? 'Reasignar' : 'Asignar' }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-row>
        <!-- ── PASO 1 — Identificación y contacto ──────────────────────────── -->
        <v-col cols="12" md="6">
          <v-card tile class="fill-height">
            <v-card-title class="subtitle-2 font-weight-bold pb-0">
              <v-icon left color="#031D6A">mdi-numeric-1-circle</v-icon>
              Identificación y contacto
            </v-card-title>
            <v-divider class="mt-2"></v-divider>
            <v-card-text class="pt-2">
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="text--secondary" width="45%">DPI</td>
                    <td><strong>{{ gestion.dpi || '—' }}</strong></td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Validación RENAP (FEEL)</td>
                    <td>
                      <v-chip v-if="gestion.validacion_fel_gt === true" color="green" dark x-small>
                        <v-icon left x-small>mdi-card-account-details-outline</v-icon>Validado
                      </v-chip>
                      <v-chip v-else-if="gestion.validacion_fel_gt === false" color="red" dark x-small>
                        <v-icon left x-small>mdi-card-remove-outline</v-icon>No validado
                      </v-chip>
                      <span v-else class="text--disabled">Sin validar</span>
                    </td>
                  </tr>
                  <tr v-if="felCui.nombre && felCui.nombre !== 'NA'">
                    <td class="text--secondary">Nombre RENAP</td>
                    <td class="text-caption">{{ felCui.nombre }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Nombre completo</td>
                    <td>{{ gestion.deudor_nombre_completo || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Teléfono</td>
                    <td>{{ gestion.numero_de_celular || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Correo</td>
                    <td>{{ gestion.correo_electronico || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Canal OTP</td>
                    <td><v-chip x-small outlined>{{ gestion.metodo_verificacion_otp || '—' }}</v-chip></td>
                  </tr>
                  <tr>
                    <td class="text--secondary">País</td>
                    <td>{{ gestion.pais || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Canal de creación</td>
                    <td><v-chip x-small color="#031D6A" dark>{{ gestion.canal_de_creacion || '—' }}</v-chip></td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Términos aceptados</td>
                    <td>{{ fnDateTime(gestion.terminos_aceptados_at) || '—' }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ── PASO 2 — Verificación OTP ───────────────────────────────────── -->
        <v-col cols="12" md="6">
          <v-card tile class="fill-height">
            <v-card-title class="subtitle-2 font-weight-bold pb-0">
              <v-icon left color="#031D6A">mdi-numeric-2-circle</v-icon>
              Verificación OTP
            </v-card-title>
            <v-divider class="mt-2"></v-divider>
            <v-card-text class="pt-2">
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="text--secondary" width="45%">Estado OTP</td>
                    <td>
                      <v-chip v-if="gestion.otp_verificado_at" color="green" dark x-small>
                        <v-icon left x-small>mdi-check</v-icon> Verificado
                      </v-chip>
                      <v-chip v-else color="grey" dark x-small>Pendiente</v-chip>
                    </td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Fecha verificación</td>
                    <td>{{ fnDateTime(gestion.otp_verificado_at) || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Canal usado</td>
                    <td>{{ gestion.otp_canal_usado || '—' }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ── PASO 3 — Domicilio ───────────────────────────────────────────── -->
        <v-col cols="12" md="6">
          <v-card tile class="fill-height">
            <v-card-title class="subtitle-2 font-weight-bold pb-0">
              <v-icon left color="#031D6A">mdi-numeric-3-circle</v-icon>
              Domicilio
            </v-card-title>
            <v-divider class="mt-2"></v-divider>
            <v-card-text class="pt-2">
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="text--secondary" width="45%">Dir. facturación</td>
                    <td>{{ gestion.consejera_direccion_facturacion || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Dir. entrega</td>
                    <td>{{ gestion.entrega_direccion || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Misma dirección</td>
                    <td>
                      <v-chip v-if="gestion.flag_direccion_entrega_y_facturacion_es_la_misma === true" color="green" dark x-small>Sí</v-chip>
                      <v-chip v-else-if="gestion.flag_direccion_entrega_y_facturacion_es_la_misma === false" color="orange" dark x-small>No</v-chip>
                      <span v-else class="text--disabled">—</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="text--secondary">NIT</td>
                    <td>{{ gestion.nit || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">GPS</td>
                    <td class="text-caption">{{ gestion.gestion_ubicacion_lat ? gestion.gestion_ubicacion_lat + ', ' + gestion.gestion_ubicacion_lng : '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Sector</td>
                    <td>
                      <v-chip v-if="gestion.sector" x-small outlined color="#031D6A">
                        <v-icon left x-small>mdi-map-marker-radius</v-icon>{{ gestion.sector }}
                      </v-chip>
                      <span v-else class="text--disabled">—</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Fachada válida</td>
                    <td>
                      <v-chip v-if="gestion.fachada_valida === true" color="green" dark x-small><v-icon left x-small>mdi-home-check</v-icon>Válida</v-chip>
                      <v-chip v-else-if="gestion.fachada_valida === false" color="red" dark x-small><v-icon left x-small>mdi-home-remove</v-icon>No válida</v-chip>
                      <span v-else class="text--disabled">—</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Labels fachada</td>
                    <td class="text-caption text--secondary">{{ gestion.fachada_labels || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Recibo válido</td>
                    <td>
                      <v-chip v-if="gestion.recibo_valido === true" color="green" dark x-small><v-icon left x-small>mdi-receipt-check</v-icon>Válido</v-chip>
                      <v-chip v-else-if="gestion.recibo_valido === false" color="red" dark x-small><v-icon left x-small>mdi-receipt-remove</v-icon>No válido</v-chip>
                      <span v-else class="text--disabled">—</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Labels recibo</td>
                    <td class="text-caption text--secondary">{{ gestion.recibo_labels || '—' }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ── PASO 4 — Biometría e identidad ─────────────────────────────── -->
        <v-col cols="12" md="6">
          <v-card tile class="fill-height">
            <v-card-title class="subtitle-2 font-weight-bold pb-0">
              <v-icon left color="#031D6A">mdi-numeric-4-circle</v-icon>
              Biometría e identidad
            </v-card-title>
            <v-divider class="mt-2"></v-divider>
            <v-card-text class="pt-2">

              <p class="caption text--secondary font-weight-bold mb-1">AWS Rekognition</p>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="text--secondary" width="45%">Resultado biométrico</td>
                    <td>
                      <v-chip v-if="gestion.rekognition_bio_result === 'aprobado'" color="green" dark x-small><v-icon left x-small>mdi-face-recognition</v-icon>Aprobado</v-chip>
                      <v-chip v-else-if="gestion.rekognition_bio_result === 'rechazado'" color="red" dark x-small><v-icon left x-small>mdi-face-recognition</v-icon>Rechazado</v-chip>
                      <span v-else class="text--disabled">—</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Similitud facial</td>
                    <td>
                      <strong v-if="gestion.rekognition_similarity != null">{{ parseFloat(gestion.rekognition_similarity).toFixed(2) }}%</strong>
                      <span v-else class="text--disabled">—</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="text--secondary">CUI coincide</td>
                    <td>
                      <v-chip v-if="gestion.rekognition_cui_matched === true" color="green" dark x-small>Sí</v-chip>
                      <v-chip v-else-if="gestion.rekognition_cui_matched === false" color="orange" dark x-small>No</v-chip>
                      <span v-else class="text--disabled">—</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Vencimiento DPI</td>
                    <td>{{ gestion.dpi_fecha_vencimiento || '—' }}</td>
                  </tr>
                </tbody>
              </v-simple-table>

              <p class="caption text--secondary font-weight-bold mb-1 mt-3">Nombres del deudor</p>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="text--secondary" width="45%">Primer nombre</td>
                    <td>{{ gestion.primer_nombre || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Segundo nombre</td>
                    <td>{{ gestion.segundo_nombre || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Tercer nombre</td>
                    <td>{{ gestion.tercer_nombre || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Primer apellido</td>
                    <td>{{ gestion.primer_apellido || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Segundo apellido</td>
                    <td>{{ gestion.segundo_apellido || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Apellido de casada</td>
                    <td>{{ gestion.tercer_apellido_o_casada || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Fecha de nacimiento</td>
                    <td>{{ gestion.fecha_de_nacimiento || '—' }}</td>
                  </tr>
                </tbody>
              </v-simple-table>

              <p class="caption text--secondary font-weight-bold mb-1 mt-3">Consentimiento y referencia</p>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="text--secondary" width="45%">Consentimiento buró</td>
                    <td>
                      {{ fnDateTime(gestion.consentimiento_buro_at) || '—' }}
                      <v-btn
                        v-if="gestion.consentimiento_buro_at"
                        x-small text color="primary" class="ml-2"
                        @click="fnDescargarAutorizacionBuro"
                      >
                        <v-icon small left>mdi-download</v-icon> Descargar autorización (PDF)
                      </v-btn>
                    </td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Contrato</td>
                    <td>
                      <v-btn
                        v-for="parte in 3" :key="parte"
                        x-small text color="primary" class="mr-1"
                        @click="fnDescargarContratoReconocimiento(parte)"
                      >
                        <v-icon small left>mdi-download</v-icon> Parte {{ parte }}
                      </v-btn>
                    </td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Política de privacidad y T&amp;C</td>
                    <td>
                      {{ fnDateTime(gestion.terminos_aceptados_at) || '—' }}
                      <v-btn
                        v-if="gestion.terminos_aceptados_at"
                        x-small text color="primary" class="ml-2"
                        @click="fnDescargarTerminosCondiciones"
                      >
                        <v-icon small left>mdi-download</v-icon> Descargar (PDF)
                      </v-btn>
                    </td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Código referencia</td>
                    <td>{{ gestion.codigo_referencia || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="text--secondary">Persona referida</td>
                    <td>
                      <v-chip v-if="gestion.flag_persona_referida_por_consejera" color="purple" dark x-small>Sí</v-chip>
                      <span v-else class="text--disabled">No</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ── BURÓ INTERNO ─────────────────────────────────────────────────── -->
        <v-col cols="12" v-if="gestion.resultado_evaluacion_buro_interno">
          <v-card tile>
            <v-card-title class="subtitle-2 font-weight-bold pb-0">
              <v-icon left color="#031D6A">mdi-bank-outline</v-icon>
              Buró Interno
            </v-card-title>
            <v-divider class="mt-2"></v-divider>
            <v-card-text class="pt-2">
              <div class="d-flex align-center flex-wrap mb-3" style="gap:8px">
                <span class="text--secondary">Resultado:</span>
                <v-chip :color="getColor(buroInternoResult.finalResult && buroInternoResult.finalResult.status)" :dark="getColor(buroInternoResult.finalResult && buroInternoResult.finalResult.status) !== 'grey'" small>
                  {{ (buroInternoResult.finalResult && buroInternoResult.finalResult.status) || '—' }}
                </v-chip>
                <span class="text-caption text--secondary" v-if="buroInternoResult.finalResult && buroInternoResult.finalResult.reason">
                  {{ buroInternoResult.finalResult.reason }}
                </span>
              </div>

              <!-- Punto 12 (aclaración usuario 2026-08-09): 'validations' del backend solo trae las
                   reglas que FALLAN — las que sí se cumplen solo quedan en 'details'. Se recorre
                   'details' para mostrar las 5 reglas siempre, no solo la que se incumplió. -->
              <v-simple-table dense v-if="buroInternoResult.details && Object.keys(buroInternoResult.details).length">
                <tbody>
                  <tr v-for="(valor, regla) in buroInternoResult.details" :key="regla">
                    <td class="text--secondary" style="width:35%">{{ regla }}</td>
                    <td style="width:15%">
                      <template v-if="['APROBADO', 'DENEGAR', 'REVISIÓN'].includes(valor)">
                        <v-icon v-if="getColor(valor) === 'green'" color="green" small>mdi-check-circle</v-icon>
                        <v-icon v-else-if="getColor(valor) === 'yellow'" color="yellow darken-2" small>mdi-alert-circle</v-icon>
                        <v-icon v-else-if="getColor(valor) === 'red'" color="red" small>mdi-close-circle-outline</v-icon>
                        <span class="ml-1 text-caption" :class="getColor(valor) + '--text'">{{ valor }}</span>
                      </template>
                      <span v-else class="text-caption text--secondary">{{ valor }}</span>
                    </td>
                    <td class="text-caption text--secondary">{{ buroInternoReasonFor(regla) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ── EVALUACIÓN TRANSUNION ────────────────────────────────────────── -->
        <v-col cols="12" v-if="gestion.resultado_evaluacion_equifax">
          <v-card tile>
            <v-card-title class="subtitle-2 font-weight-bold pb-0">
              <v-icon left color="#031D6A">mdi-chart-bar</v-icon>
              Evaluación TransUnion
            </v-card-title>
            <v-divider class="mt-2"></v-divider>
            <v-card-text class="pt-2">
              <!-- Resumen: resultado + score + descarga -->
              <div class="d-flex align-center flex-wrap mb-3" style="gap:8px">
                <span class="text--secondary">Resultado:</span>
                <v-chip v-if="gestion.resultado_evaluacion_equifax === 'aprobado'" color="green" dark small>
                  <v-icon left small>mdi-check-circle</v-icon> Aprobado
                </v-chip>
                <v-chip v-else-if="gestion.resultado_evaluacion_equifax === 'revisión'" color="yellow accent-3" small>
                  <v-icon left small>mdi-alert-circle</v-icon> Revisión
                </v-chip>
                <v-chip v-else-if="gestion.resultado_evaluacion_equifax === 'rechazado'" color="red" dark small>
                  <v-icon left small>mdi-close-circle</v-icon> Rechazado
                </v-chip>
                <v-chip v-else-if="gestion.resultado_evaluacion_equifax === 'NO CONSULTADO'" color="blue-grey" dark small>
                  <v-icon left small>mdi-cancel</v-icon> No consultado
                </v-chip>
                <v-chip v-else color="grey" small>{{ gestion.resultado_evaluacion_equifax }}</v-chip>

                <template v-if="gestion.resultado_evaluacion_equifax_score != null">
                  <v-divider vertical class="mx-1"></v-divider>
                  <span class="text--secondary text-caption">Score:</span>
                  <strong class="text-body-2">{{ gestion.resultado_evaluacion_equifax_score }}</strong>
                </template>

                <v-spacer></v-spacer>

                <v-btn
                  v-if="gestion.resultado_evaluacion_equifax_pdf_url"
                  x-small text color="primary"
                  :href="feathersServer + gestion.resultado_evaluacion_equifax_pdf_url"
                  target="_blank" download
                >
                  <v-icon small left>mdi-download</v-icon> Descargar XML
                </v-btn>
              </div>

              <!-- Tabla de reglas -->
              <v-simple-table dense v-if="Array.isArray(equifaxResult) && equifaxResult.length">
                <tbody>
                  <tr v-for="evaluacion in equifaxResult" :key="evaluacion.regla">
                    <td class="text--secondary" style="width:60%">{{ evaluacion.regla }}</td>
                    <td>
                      <v-icon v-if="getColor(evaluacion.resultado) === 'green'" color="green" small>mdi-check-circle</v-icon>
                      <v-icon v-else-if="getColor(evaluacion.resultado) === 'yellow'" color="yellow darken-2" small>mdi-alert-circle</v-icon>
                      <v-icon v-else-if="getColor(evaluacion.resultado) === 'red'" color="red" small>mdi-close-circle-outline</v-icon>
                      <v-icon v-else color="grey" small>mdi-minus-circle-outline</v-icon>
                      <span class="ml-1 text-caption" :class="getColor(evaluacion.resultado) + '--text'">{{ evaluacion.resultado }}</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <v-alert v-else-if="typeof equifaxResult === 'string' && equifaxResult" type="info" dense text class="mb-0 mt-2">
                {{ equifaxResult }}
              </v-alert>

              <!-- Informe completo desde XML -->
              <template v-if="gestion.resultado_evaluacion_equifax_pdf_url">
                <v-divider class="my-3"></v-divider>
                <v-expansion-panels flat>
                  <v-expansion-panel>
                    <v-expansion-panel-header class="px-0 subtitle-2 font-weight-bold text--secondary">
                      <span><v-icon small left>mdi-file-document-outline</v-icon>Informe completo TransUnion</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-0">
                      <gestion-buro-transunion
                        :prop_xml="feathersServer + gestion.resultado_evaluacion_equifax_pdf_url"
                      ></gestion-buro-transunion>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </template>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ── COMPLEMENTOS — Fotos ─────────────────────────────────────────── -->
        <v-col cols="12">
          <v-card tile>
            <v-card-title class="subtitle-2 font-weight-bold pb-0">
              <v-icon left color="#031D6A">mdi-image-multiple</v-icon>
              Complementos fotográficos
            </v-card-title>
            <v-divider class="mt-2"></v-divider>
            <v-card-text>
              <div v-if="loadingComplementos" class="text-center py-6">
                <v-progress-circular indeterminate color="#031D6A"></v-progress-circular>
                <p class="mt-2 text-caption text--secondary">Cargando complementos...</p>
              </div>

              <v-row v-else>
                <v-col v-for="(comp, i) in complementos" :key="i" cols="6" sm="4" md="3" lg="2">
                  <v-card outlined class="text-center">
                    <v-card-text class="pa-2">
                      <p class="caption font-weight-bold text--secondary mb-2">{{ comp.titulo }}</p>

                      <div v-if="comp.src" @click="fnAbrirFoto(comp)" style="cursor:pointer;">
                        <v-img
                          :src="comp.src"
                          max-height="120"
                          contain
                          class="grey lighten-4"
                        >
                          <template v-slot:placeholder>
                            <v-row class="fill-height ma-0" align="center" justify="center">
                              <v-progress-circular indeterminate color="grey lighten-2" size="24"></v-progress-circular>
                            </v-row>
                          </template>
                        </v-img>
                        <p class="caption mt-1 text--secondary">Click para ampliar</p>
                      </div>

                      <div v-else class="py-6">
                        <v-icon size="40" color="grey lighten-2">mdi-image-off-outline</v-icon>
                        <p class="caption text--disabled mt-1">No cargada</p>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ── COMENTARIOS ──────────────────────────────────────────────────── -->
        <v-col cols="12">
          <v-card tile>
            <v-card-title class="subtitle-2 font-weight-bold pb-0">
              <v-icon left color="#031D6A">mdi-comment-text-multiple</v-icon>
              Comentarios
            </v-card-title>
            <v-divider class="mt-2"></v-divider>
            <v-card-text>
              <v-textarea
                v-model="nuevoComentario"
                label="Escribir un comentario..."
                rows="2"
                auto-grow
                outlined
                dense
                hide-details
                class="mb-2"
              ></v-textarea>
              <v-row align="center" no-gutters class="mb-4">
                <v-col cols="auto" class="mr-4">
                  <v-checkbox
                    v-model="notificarAgente"
                    :disabled="!gestion.agente_de_comercio_asignado_id"
                    label="Notificar al móvil del agente asignado"
                    dense
                    hide-details
                    class="mt-0"
                  ></v-checkbox>
                  <span
                    v-if="!gestion.agente_de_comercio_asignado_id"
                    class="text-caption text--disabled"
                  >
                    La gestión no tiene agente de comercio asignado
                  </span>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto">
                  <v-btn
                    small
                    color="#031D6A"
                    dark
                    :loading="guardandoComentario"
                    :disabled="!nuevoComentario || !nuevoComentario.trim()"
                    @click="fnAgregarComentario"
                  >
                    <v-icon left small>mdi-send</v-icon>
                    Agregar comentario
                  </v-btn>
                </v-col>
              </v-row>

              <div v-if="loadingComentarios" class="text-center py-4">
                <v-progress-circular indeterminate color="#031D6A" size="24"></v-progress-circular>
              </div>

              <p v-else-if="!comentarios.length" class="text-caption text--disabled mb-0">
                Sin comentarios para esta gestión.
              </p>

              <template v-else>
                <div
                  v-for="com in comentarios"
                  :key="com.id"
                  class="mb-3 pa-3 grey lighten-4 rounded"
                >
                  <v-row align="center" no-gutters class="mb-1">
                    <v-col cols="auto" class="mr-2">
                      <v-icon small color="#031D6A">mdi-account-circle</v-icon>
                      <strong class="ml-1">{{ com.CREADO_POR_USUARIO_NOMBRE || com.CREADO_POR_USUARIO }}</strong>
                    </v-col>
                    <v-col cols="auto" class="mr-2">
                      <span class="text-caption text--secondary">{{ fnFechaAsignacion(com.createdAt) }}</span>
                    </v-col>
                    <v-col cols="auto">
                      <v-chip v-if="com.flag_comentario_externo == 1" x-small outlined color="#031D6A">
                        <v-icon left x-small>mdi-cellphone-message</v-icon>
                        Notificado al móvil
                      </v-chip>
                    </v-col>
                  </v-row>
                  <p class="mb-0" style="white-space: pre-wrap;">{{ com.comentario }}</p>
                </div>
              </template>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- ── VISITA DE SUPERVISORA (puntos 17/18, solo lectura por ahora) ──────
             Solo visible para el usuario dtk: se liberará al resto cuando se apruebe
             la cotización de la pantalla de captura. -->
        <v-col v-if="currentUser.usuario === 'dtk'" cols="12">
          <v-card tile>
            <v-card-title class="subtitle-2 font-weight-bold pb-0">
              <v-icon left color="#031D6A">mdi-map-marker-account</v-icon>
              Visita de supervisora
            </v-card-title>
            <v-divider class="mt-2"></v-divider>
            <v-card-text>
              <div v-if="loadingVisitasSupervisora" class="text-center py-4">
                <v-progress-circular indeterminate color="#031D6A" size="24"></v-progress-circular>
              </div>

              <p v-else-if="!visitasSupervisora.length" class="text-caption text--disabled mb-0">
                Aún no hay visitas registradas para esta gestión.
              </p>

              <template v-else>
                <div
                  v-for="visita in visitasSupervisora"
                  :key="visita.id"
                  class="mb-3 pa-3 grey lighten-4 rounded"
                >
                  <v-row align="center" no-gutters class="mb-1">
                    <v-col cols="auto" class="mr-2">
                      <v-icon small color="#031D6A">mdi-account-circle</v-icon>
                      <strong class="ml-1">{{ visita.CREADO_POR_USUARIO_NOMBRE || visita.CREADO_POR_USUARIO }}</strong>
                    </v-col>
                    <v-col cols="auto">
                      <span class="text-caption text--secondary">{{ fnFechaAsignacion(visita.createdAt) }}</span>
                    </v-col>
                  </v-row>
                  <p v-if="visita.comentario" class="mb-2" style="white-space: pre-wrap;">{{ visita.comentario }}</p>
                  <v-row align="center" no-gutters>
                    <v-col cols="auto" class="mr-4">
                      <a
                        v-if="visita.lat && visita.lng"
                        :href="`https://www.google.com/maps?q=${visita.lat},${visita.lng}`"
                        target="_blank"
                        rel="noopener"
                        class="text-caption"
                      >
                        <v-icon x-small color="#031D6A">mdi-map-marker</v-icon>
                        {{ visita.lat }}, {{ visita.lng }}
                      </a>
                      <span v-else class="text-caption text--disabled">Sin coordenada GPS</span>
                    </v-col>
                    <v-col cols="auto" class="mr-4">
                      <a v-if="visita.fotografia" href="#" class="text-caption" @click.prevent="fnAbrirFotoVisita(visita)">
                        <v-icon x-small color="#031D6A">mdi-image</v-icon>
                        Ver fotografía
                      </a>
                      <span v-else class="text-caption text--disabled">Sin fotografía</span>
                    </v-col>
                    <v-col cols="auto">
                      <a
                        v-if="fnIdEvaluacionRelacionada(visita)"
                        :href="fnLinkEvaluacionRelacionada(visita)"
                        target="_blank"
                        rel="noopener"
                        class="text-caption"
                      >
                        <v-icon x-small color="#031D6A">mdi-file-document-outline</v-icon>
                        Evaluación relacionada: {{ visita.numero_evaluacion_relacionada }}
                      </a>
                      <span v-else-if="visita.numero_evaluacion_relacionada" class="text-caption">
                        <v-icon x-small color="#031D6A">mdi-file-document-outline</v-icon>
                        Evaluación relacionada: {{ visita.numero_evaluacion_relacionada }}
                      </span>
                    </v-col>
                  </v-row>
                </div>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- ── Dialog foto ampliada ────────────────────────────────────────────── -->
      <v-dialog v-model="dialogFoto" width="900" persistent>
        <v-card v-if="fotoActiva">
          <v-card-title>{{ fotoActiva.titulo }}</v-card-title>
          <v-card-text>
            <v-img :src="fotoActiva.src" contain max-height="600" />
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn small color="#031D6A" dark :href="fotoActiva.src" download>
              <v-icon left small>mdi-download</v-icon> Descargar
            </v-btn>
            <v-btn text small @click="dialogFoto = false" class="ml-2">Cerrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ── Dialog asignar/reasignar agente de comercio ─────────────────────── -->
      <v-dialog v-model="dialogAsignarAgente" width="480" persistent>
        <v-card>
          <v-card-title class="subtitle-2 font-weight-bold">Asignar agente de comercio</v-card-title>
          <v-card-text>
            <v-autocomplete
              v-model="usuarioSeleccionado"
              :items="usuariosBusqueda"
              :loading="loadingUsuarios"
              :search-input.sync="searchUsuarioInput"
              @update:search-input="fnBuscarUsuarios"
              label="Buscar usuario (nombre o usuario)"
              :item-text="fnUsuarioItemText"
              item-value="id"
              return-object
              no-filter
              clearable
              no-data-text="Sin resultados"
            ></v-autocomplete>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text small :disabled="guardandoAsignacion" @click="dialogAsignarAgente = false">Cancelar</v-btn>
            <v-btn
              small
              color="#031D6A"
              dark
              :loading="guardandoAsignacion"
              :disabled="!usuarioSeleccionado"
              @click="fnGuardarAsignacion"
            >
              Guardar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </v-container>
</template>

<script>
import moment from 'moment';
require('moment/locale/es.js');
import axios from 'axios';
import { mapActions } from 'vuex';
import feathersClient from '../../feathers-client';
import GestionBuroTransunion from '../gestion_buro_transunion.vue';

export default {
  name: 'detalleReconocimientoFacial',
  components: { GestionBuroTransunion },
  data() {
    return {
      loading: true,
      loadingComplementos: false,
      gestion: null,
      complementos: [],
      dialogFoto: false,
      fotoActiva: null,
      dialogAsignarAgente: false,
      usuariosBusqueda: [],
      usuarioSeleccionado: null,
      searchUsuarioInput: null,
      loadingUsuarios: false,
      guardandoAsignacion: false,
      comentarios: [],
      loadingComentarios: false,
      nuevoComentario: '',
      notificarAgente: false,
      guardandoComentario: false,
      visitasSupervisora: [],
      loadingVisitasSupervisora: false,
    };
  },
  computed: {
    feathersServer() {
      return process.env.VUE_APP_FEATHERS_SERVER;
    },
    currentUser() {
      return this.$store.state.auth.payload.usuario;
    },
    equifaxResult() {
      try {
        const parsed = JSON.parse(this.gestion && this.gestion.resultado_evaluacion_equifax_detalle);
        return parsed || {};
      } catch (e) {
        return (this.gestion && this.gestion.resultado_evaluacion_equifax_detalle) || {};
      }
    },
    // Punto 12 (2026-08-09): explica el resultado cuando TransUnion no se consultó
    // (ej. la persona ya existe en el buró interno) — antes no había forma de ver el porqué.
    buroInternoResult() {
      try {
        return JSON.parse(this.gestion && this.gestion.resultado_evaluacion_buro_interno) || {};
      } catch (e) {
        return {};
      }
    },
    // Datos de la persona según RENAP (certificador FEEL), guardados por el
    // canal web en validacion_fel_gt_response al validar el CUI en el Step 1.
    felCui() {
      try {
        const parsed = JSON.parse(this.gestion && this.gestion.validacion_fel_gt_response);
        return (parsed && parsed.cui) || {};
      } catch (e) {
        return {};
      }
    },
  },
  methods: {
    getColor(estado) {
      if (['rechazado', 'RECHAZADO', 'DENEGADO', 'DENEGAR'].includes(estado)) return 'red';
      if (['REVISIÓN', 'revision', 'revisión'].includes(estado)) return 'yellow';
      if (['aprobado', 'APROBADO', 'APROBADO AUTOMATICO'].includes(estado)) return 'green';
      return 'grey';
    },
    // Los nombres de regla en 'details' no siempre coinciden exacto con 'validations[].rule'
    // (ej. 'Estado del Crédito (DENEGAR)' vs 'Estado del Crédito') — match por prefijo en ambos sentidos.
    buroInternoReasonFor(reglaKey) {
      const validaciones = (this.buroInternoResult && this.buroInternoResult.validations) || [];
      const match = validaciones.find((v) => reglaKey.startsWith(v.rule) || v.rule.startsWith(reglaKey));
      return match ? match.reason : '';
    },
    fnDateTime(date) {
      return date ? moment(date).format('DD-MM-YYYY HH:mm:ss') : '';
    },
    fnAbrirFoto(comp) {
      this.fotoActiva = comp;
      this.dialogFoto = true;
    },
    fnFotoSrc(path) {
      if (!path) return null;
      return process.env.VUE_APP_FEATHERS_SERVER + path;
    },

    fnDescargarAutorizacionBuro() {
      const url = `${this.feathersServer}generar-autorizacion-buro-pdf/${this.gestion.id}`;
      axios.get(url, {
        responseType: 'blob',
        headers: { Authorization: 'Bearer ' + window.localStorage.getItem('feathers-jwt') },
      }).then((response) => {
        const urlDown = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = urlDown;
        link.setAttribute('download', `autorizacion-buro-gestion-${this.gestion.id}.pdf`);
        document.body.appendChild(link);
        link.click();
      }).catch((err) => {
        console.error('Error al descargar autorización de buró:', err);
      });
    },

    // Política de privacidad + T&C con el nombre de la persona ya escrito en la cláusula de
    // consentimiento expreso (D-01/D-02 de la sesión de debugging 2026-07-29: solo nombre, sin firma).
    fnDescargarTerminosCondiciones() {
      const url = `${this.feathersServer}generar-terminos-condiciones-pdf/${this.gestion.id}`;
      axios.get(url, {
        responseType: 'blob',
        headers: { Authorization: 'Bearer ' + window.localStorage.getItem('feathers-jwt') },
      }).then((response) => {
        const urlDown = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = urlDown;
        link.setAttribute('download', `terminos-condiciones-gestion-${this.gestion.id}.pdf`);
        document.body.appendChild(link);
        link.click();
      }).catch((err) => {
        console.error('Error al descargar términos y condiciones:', err);
      });
    },

    // Contrato: mismo generador (PDFKit sobre plantilla) que usa el panel para gestiones del
    // flujo tradicional (vistaContratoDeudor.vue), apuntado a la gestión de reconocimiento facial
    // vía ?origen=reconocimiento (no toca el comportamiento legacy cuando ese parámetro no viene).
    fnDescargarContratoReconocimiento(pagina) {
      const url = `${this.feathersServer}generar-contrato-fisico-pdf/${this.gestion.id}?pagina=${pagina}&origen=reconocimiento`;
      axios.get(url, {
        responseType: 'blob',
        headers: { Authorization: 'Bearer ' + window.localStorage.getItem('feathers-jwt') },
      }).then((response) => {
        const urlDown = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = urlDown;
        link.setAttribute('download', `contrato-gestion-${this.gestion.id}-parte-${pagina}.pdf`);
        document.body.appendChild(link);
        link.click();
      }).catch((err) => {
        console.error('Error al descargar contrato:', err);
      });
    },

    fnFechaAsignacion(date) {
      return date ? moment(date).format('DD-MM-YYYY HH:mm a') : '';
    },
    fnUsuarioItemText(u) {
      return u.nombre + ' (' + u.usuario + ')';
    },
    fnAbrirDialogAsignarAgente() {
      this.usuarioSeleccionado = null;
      this.usuariosBusqueda = [];
      this.dialogAsignarAgente = true;
      this.fnBuscarUsuarios('');
    },
    fnBuscarUsuarios(val) {
      const params = {
        STATUS: 1,
        $limit: 25,
        $sort: { nombre: 1 },
      };
      if (val) {
        params['$or'] = [
          { nombre:  { $like: '%' + val + '%' } },
          { usuario: { $like: '%' + val + '%' } },
        ];
      }
      this.loadingUsuarios = true;
      axios
        .get(process.env.VUE_APP_FEATHERS_SERVER + 'usuarios', {
          params,
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + window.localStorage.getItem('feathers-jwt'),
          },
        })
        .then((response) => {
          this.usuariosBusqueda = response.data.data;
          this.loadingUsuarios = false;
        })
        .catch(() => {
          this.loadingUsuarios = false;
        });
    },
    async fnGuardarAsignacion() {
      if (!this.usuarioSeleccionado) return;
      this.guardandoAsignacion = true;
      try {
        const payload = {
          agente_de_comercio_asignado_id:             this.usuarioSeleccionado.id,
          agente_de_comercio_asignado_nombre:         this.usuarioSeleccionado.nombre,
          agente_de_comercio_asignado_por_usuario:    this.currentUser.nombre,
          agente_de_comercio_asignado_por_usuario_id: this.currentUser.id,
          agente_de_comercio_asignado_fecha:          moment().utc().format('YYYY-MM-DDTHH:mm:ss.SSSS+00:00'),
        };
        const response = await axios.patch(
          process.env.VUE_APP_FEATHERS_SERVER + 'gestiones-reconocimiento-facial/' + this.gestion.id,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + window.localStorage.getItem('feathers-jwt'),
            },
          }
        );
        this.gestion = Object.assign({}, this.gestion, response.data);
        this.dialogAsignarAgente = false;
      } catch (err) {
        console.error('Error al asignar agente de comercio:', err);
      } finally {
        this.guardandoAsignacion = false;
      }
    },

    fnCargarComentarios() {
      this.loadingComentarios = true;
      axios
        .get(process.env.VUE_APP_FEATHERS_SERVER + 'comentarios-por-gestion-reconocimiento', {
          params: {
            gestion_reconocimiento_id: this.gestion.id,
            $limit: 100,
            $sort: { createdAt: -1 },
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + window.localStorage.getItem('feathers-jwt'),
          },
        })
        .then((response) => {
          this.comentarios = response.data.data;
          this.loadingComentarios = false;
        })
        .catch((err) => {
          console.error('Error al cargar comentarios:', err);
          this.loadingComentarios = false;
        });
    },
    // Puntos 17/18 (feedback 2026-07-29): solo lectura por ahora — la captura
    // (GPS + foto + comentario) todavía no tiene pantalla, se registra vía API
    // directa (móvil o manual) hasta que se defina quién construye la captura.
    fnCargarVisitasSupervisora() {
      this.loadingVisitasSupervisora = true;
      axios
        .get(process.env.VUE_APP_FEATHERS_SERVER + 'visitas-supervisora-por-gestion-reconocimiento', {
          params: {
            gestion_reconocimiento_id: this.gestion.id,
            $limit: 100,
            $sort: { createdAt: -1 },
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + window.localStorage.getItem('feathers-jwt'),
          },
        })
        .then((response) => {
          this.visitasSupervisora = response.data.data;
          this.loadingVisitasSupervisora = false;
        })
        .catch((err) => {
          console.error('Error al cargar visitas de supervisora:', err);
          this.loadingVisitasSupervisora = false;
        });
    },
    fnAbrirFotoVisita(visita) {
      this.fotoActiva = { titulo: 'Visita de supervisora', src: this.fnFotoSrc(visita.fotografia) };
      this.dialogFoto = true;
    },
    fnIdEvaluacionRelacionada(visita) {
      if (!visita.numero_evaluacion_relacionada) return null;
      const match = String(visita.numero_evaluacion_relacionada).match(/\d+/);
      return match ? match[0] : null;
    },
    fnLinkEvaluacionRelacionada(visita) {
      const id = this.fnIdEvaluacionRelacionada(visita);
      if (!id) return null;
      return this.$router.resolve(`/detalle-gestion/${id}`).href;
    },
    async fnAgregarComentario() {
      if (!this.nuevoComentario || !this.nuevoComentario.trim()) return;
      this.guardandoComentario = true;
      try {
        const payload = {
          gestion_reconocimiento_id: this.gestion.id,
          comentario: this.nuevoComentario.trim(),
          flag_comentario_externo:
            this.notificarAgente && this.gestion.agente_de_comercio_asignado_id ? 1 : 0,
          CREADO_POR_USUARIO: this.currentUser.usuario,
          CREADO_POR_USUARIO_NOMBRE: this.currentUser.nombre,
          CREADO_POR_USUARIO_ID: this.currentUser.id,
        };
        const response = await axios.post(
          process.env.VUE_APP_FEATHERS_SERVER + 'comentarios-por-gestion-reconocimiento',
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + window.localStorage.getItem('feathers-jwt'),
            },
          }
        );
        this.comentarios.unshift(response.data);
        this.nuevoComentario = '';
      } catch (err) {
        console.error('Error al agregar comentario:', err);
      } finally {
        this.guardandoComentario = false;
      }
    },

    ...mapActions('FotografiasDeCasa', { getFotografiasDeCasa: 'get' }),
    ...mapActions('Recibos',           { getRecibos:           'get' }),
    ...mapActions('DocumentosDeIdentificacion', { getDocumentosDeIdentificacion: 'get' }),

    async loadComplementos() {
      this.loadingComplementos = true;
      const g = this.gestion;

      const result = [
        { titulo: 'DPI frontal',      src: null },
        { titulo: 'DPI reverso',      src: null },
        { titulo: 'Selfie con DPI',   src: null },
        { titulo: 'Fachada',          src: null },
        { titulo: 'Recibo servicio',  src: null },
        { titulo: 'Firma',            src: null },
      ];

      await Promise.all([
        g.documento_de_identificacion_id
          ? this.getDocumentosDeIdentificacion(g.documento_de_identificacion_id)
              .then(r => {
                result[0].src = this.fnFotoSrc(r.documento_de_identificacion_frontal);
                result[1].src = this.fnFotoSrc(r.documento_de_identificacion_anverso);
                result[2].src = this.fnFotoSrc(r.documento_de_identificacion_con_persona);
              })
              .catch(() => {})
          : Promise.resolve(),

        g.fotografia_de_casa_id
          ? this.getFotografiasDeCasa(g.fotografia_de_casa_id)
              .then(r => { result[3].src = this.fnFotoSrc(r.fotografia); })
              .catch(() => {})
          : Promise.resolve(),

        g.recibo_de_servicio_id
          ? this.getRecibos(g.recibo_de_servicio_id)
              .then(r => { result[4].src = this.fnFotoSrc(r.fotografia); })
              .catch(() => {})
          : Promise.resolve(),

        g.firma_deudor_id
          ? feathersClient.service('imagenes-de-firmas').get(g.firma_deudor_id)
              .then(r => { result[5].src = this.fnFotoSrc(r.fotografia); })
              .catch(() => {})
          : Promise.resolve(),
      ]);

      this.complementos = result;
      this.loadingComplementos = false;
    },

    ...mapActions('GestionesReconocimientoFacial', { findGestiones: 'find' }),

    async main() {
      this.loading = true;
      try {
        const id = parseInt(this.$route.params.id);
        const result = await this.findGestiones({ query: { id, $limit: 1 } });
        this.gestion = result.data && result.data.length ? result.data[0] : null;
        if (this.gestion) {
          this.loadComplementos();
          this.fnCargarComentarios();
          if (this.currentUser.usuario === 'dtk') {
            this.fnCargarVisitasSupervisora();
          }
        }
      } catch (err) {
        console.error('detalleReconocimientoFacial error:', err);
        this.gestion = null;
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.main();
  },
};
</script>
